import { type AnchorHTMLAttributes, type MouseEvent, type ReactNode, useSyncExternalStore } from "react";

export type Route =
  | { name: "login" }
  | { name: "loginToken"; token: string }
  | { name: "invite"; token: string }
  | { name: "start"; domain: string | null }
  | { name: "setup" }
  | { name: "activity" }
  | { name: "message"; id: string }
  | { name: "actions" }
  | { name: "actionNew"; from: ActionNewOrigin }
  | { name: "actionEdit"; id: string }
  | { name: "approvals" }
  | { name: "settings" };

export type RouteName = Route["name"];

/** The screen that sent the member to /actions/new, and where it returns after a create or a cancel. */
export type ActionNewOrigin = "actions" | "setup";

/** Screens that work without a session. */
export const PUBLIC_ROUTES: ReadonlySet<RouteName> = new Set(["login", "loginToken", "invite"]);

const NAVIGATE_EVENT = "hooky:navigate";

export function parseLocation(pathname: string, search: string): Route {
  const query = new URLSearchParams(search);
  const token = query.get("token");
  const segments = pathname.split("/").filter((segment) => segment !== "");
  const [first, second] = segments;

  if (first === "login") return token === null ? { name: "login" } : { name: "loginToken", token };
  if (first === "invite" && token !== null) return { name: "invite", token };
  if (first === "start") return { name: "start", domain: query.get("domain") };
  if (first === "setup") return { name: "setup" };
  if (first === "messages" && second !== undefined) return { name: "message", id: decodeURIComponent(second) };
  if (first === "actions" && second === "new") return { name: "actionNew", from: query.get("from") === "setup" ? "setup" : "actions" };
  if (first === "actions" && second !== undefined) return { name: "actionEdit", id: decodeURIComponent(second) };
  if (first === "actions") return { name: "actions" };
  if (first === "approvals") return { name: "approvals" };
  if (first === "settings") return { name: "settings" };
  return { name: "activity" };
}

export function navigate(path: string, options: { replace: boolean } = { replace: false }): void {
  if (options.replace) window.history.replaceState(null, "", path);
  else window.history.pushState(null, "", path);
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
  window.scrollTo({ top: 0 });
}

/** Calls `listener` after every navigation. Returns a function that removes it. */
export function subscribe(listener: () => void): () => void {
  window.addEventListener("popstate", listener);
  window.addEventListener(NAVIGATE_EVENT, listener);
  return () => {
    window.removeEventListener("popstate", listener);
    window.removeEventListener(NAVIGATE_EVENT, listener);
  };
}

function currentHref(): string {
  return `${window.location.pathname}${window.location.search}`;
}

/** The current location, re-rendered on every navigation. */
export function useRoute(): Route {
  const href = useSyncExternalStore(subscribe, currentHref);
  const url = new URL(href, window.location.origin);
  return parseLocation(url.pathname, url.search);
}

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> {
  to: string;
  children: ReactNode;
}

export function Link({ to, children, onClick, ...rest }: LinkProps) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>): void {
    onClick?.(event);
    if (event.defaultPrevented || !isPlainLeftClick(event)) return;
    event.preventDefault();
    navigate(to);
  }
  return (
    <a href={to} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
